import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { ConstColor } from "src/config/styles_const/styles_const";
import { createContractCustomerReportAPI } from "src/infra/api/service/contract_customer_report_api";
import GoldLink from "src/presentation/common/atom/btn/gold_link";
import { toDateString, toYMString, ymToDate } from "src/util/commonFuntion";
import styled from "styled-components";
import { routeConfig } from "../../config/language/ja-JP/presentation/route_text";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "../../domain/model/app_notification_message";
import { FrontDate } from "../../domain/model/frontDate";
import { IContractRepository } from "../../domain/repository/contract_repository";
import { IApiClient, createApiClient } from "../../infra/api/client";
import { entity } from "../../infra/api/rpc/api";
import { createContractAPI } from "../../infra/api/service/contract_api";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import AppNotificationWithTexts from "../common/organism/app_notification_with_texts";
import Progress from "../common/organism/progress";
import { TBreadcrumbItem } from "../type/common";

const apiClient: IApiClient = createApiClient();
const contractRepository: IContractRepository = createContractAPI(apiClient);
const reportRepository = createContractCustomerReportAPI(apiClient);

function generateYearMonthList(start: string): string[] {
  const startDate = ymToDate(start);
  const currentDate = new Date();
  currentDate.setMonth(currentDate.getMonth() - 1);

  const date = startDate;
  const list: string[] = [];

  while (date <= currentDate) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    list.push(`${year}-${month}`);
    date.setMonth(date.getMonth() + 1);
  }

  return list;
}

interface IProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const CustomerContractOperationDetail: React.FC<IProps> = (props) => {
  const [contract, setContract] = React.useState<
    entity.IContract | undefined
  >();
  const [reports, setReports] = React.useState<
    Map<string, entity.IContractCustomerReport> | undefined
  >();

  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showAlertBox, setShowAlertBox] = React.useState<boolean>(false);

  React.useEffect(() => {
    try {
      contractRepository.getOperation(props.match.params.id).then((res) => {
        setContract(res);
        setShowAlertBox(!res.operation!!.reportCompleted || false);
      });
      reportRepository.getAllByContract(props.match.params.id).then((res) => {
        const m = new Map<string, entity.IContractCustomerReport>();
        res.forEach((item) => {
          m.set(item.targetYM!, item);
        });
        setReports(m);
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.customerRoot,
    { text: `稼働報告` },
  ];

  if (!contract || !contract.operation) {
    return <Progress canAnimation={false} />;
  }

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}

      {isLoading && <Progress canAnimation={false} />}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />

        <PageTitle>稼働報告一覧</PageTitle>

        {showAlertBox && (
          <AlertBox>
            稼働報告が未入力の契約があります。稼働報告ボタンより、入力をお願い致します。
          </AlertBox>
        )}

        <ItemLabel>対象の成約</ItemLabel>
        <Item>
          <ItemSection>
            {contract.operation.status ===
              entity.ContractOperation.Status.Status_InProgress && (
              <InProgressLabel>稼働中</InProgressLabel>
            )}
            {contract.operation.status ===
              entity.ContractOperation.Status.Status_Completed && (
              <EndLabel>稼働終了</EndLabel>
            )}
            <ProjectNameLabel
              target="_blank"
              to={`/customer/project/${contract.projectID}`}
            >
              {contract.projectName}
            </ProjectNameLabel>
          </ItemSection>

          <DateContainer>
            <DateLabel>
              稼働開始日: {toDateString(contract.operation.startedDate || "")}
            </DateLabel>

            {contract.operation.status ===
              entity.ContractOperation.Status.Status_InProgress && (
              <DateLabel>
                稼働終了予定:{" "}
                {toYMString(contract.operation.endScheduledYM || "")}
              </DateLabel>
            )}
            {contract.operation.status ===
              entity.ContractOperation.Status.Status_Completed && (
              <DateLabel>
                稼働終了日: {toDateString(contract.operation.endedDate || "")}
              </DateLabel>
            )}
          </DateContainer>

          <ItemSection>
            <CompanyInfo>
              <CompanyName>{contract.companyName}</CompanyName>
            </CompanyInfo>
          </ItemSection>
        </Item>

        {reports && (
          <List>
            {generateYearMonthList(contract.operation.startedDate || "")
              .reverse()
              .map((ym, i) => {
                const report = reports.get(ym);

                if (report) {
                  return (
                    <OperationItem key={i}>
                      <OperationIcon>📌</OperationIcon>

                      <OperationBody>
                        <OperationInputedLabel>送信完了</OperationInputedLabel>
                        <OperationDateLabel>
                          {toDateString(report.createdAt || "")}
                        </OperationDateLabel>
                        <OperationTitleLabel>
                          {toYMString(report.targetYM || "")}分の稼働報告
                        </OperationTitleLabel>
                      </OperationBody>

                      <ReportDetailLink
                        to={`/customer/operations/${contract.id}/report/${report.id}`}
                      >
                        確認する
                      </ReportDetailLink>
                    </OperationItem>
                  );
                } else {
                  return (
                    <OperationItem key={i}>
                      <OperationIcon>🙏</OperationIcon>

                      <OperationBody>
                        <OperationNotInputLabel>未入力</OperationNotInputLabel>
                        <OperationTitleLabel>
                          {toYMString(ym)}分の稼働報告
                        </OperationTitleLabel>
                      </OperationBody>

                      <ReportDetailLink
                        to={`/customer/operations/${contract.id}/report_registration/${ym}`}
                      >
                        入力する
                      </ReportDetailLink>
                    </OperationItem>
                  );
                }
              })}
          </List>
        )}
      </PageWrapper>
    </React.Fragment>
  );
};

export default CustomerContractOperationDetail;

const PageTitle = styled.div`
  font-size: 26px;
  font-weight: bold;
  color: #333333;
  margin-top: 30px;
  margin-bottom: 30px;
`;

const AlertBox = styled.div`
  width: 100%;
  padding: 20px;
  background: #ce414133;
  color: #333;
  margin-top: 30px;
  margin-bottom: 40px;
  font-size: 12px;
  font-weight: bold;
  border-radius: 4px;
`;

const ItemLabel = styled.div`
  font-size: 13px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 12px;
`;

const Item = styled.div`
  padding: 5px 5px;
  width: 100%;
  border-bottom: 1px solid ${ConstColor.PlaceholderAndBorder};
`;

const ItemSection = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0px;
`;

const EndLabel = styled.div`
  margin-right: 10px;
  background: #a4a4a4;
  border-radius: 4px;
  padding: 4px 15px;
  font-size: 9px;
  font-weight: bold;
  color: white;
`;

const InProgressLabel = styled.div`
  margin-right: 10px;
  background: ${ConstColor.Success};
  border-radius: 4px;
  padding: 4px 15px;
  font-size: 9px;
  font-weight: bold;
  color: white;
`;

const DateContainer = styled.div`
  display: flex;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const DateLabel = styled.div`
  font-size: 11px;
  color: #333333;
  margin-right: 45px;
`;

const ProjectNameLabel = styled(Link)`
  font-size: 11px;
  font-weight: bold;
  color: #333333;
  margin-right: 7px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  ::after {
    font-family: "Font Awesome 5 Free";
    content: "\f35d";
    margin-left: 5px;
  }
`;

const CompanyInfo = styled.div`
  flex-grow: 1;
  padding: 0px 10px;
`;

const CompanyName = styled.div`
  font-size: 15px;
  font-weight: bold;
  color: #333333;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 24px;
`;

const List = styled.div`
  width: 100%;
`;

const OperationItem = styled.div`
  padding: 15px 25px;
  width: 100%;
  border-bottom: 1px solid ${ConstColor.PlaceholderAndBorder};
  display: flex;
  align-items: flex-end;
`;

const OperationIcon = styled.div`
  font-size: 35px;
  margin-right: 25px;
`;

const OperationBody = styled.div`
  flex-grow: 1;
`;

const OperationInputedLabel = styled.div`
  margin-right: 10px;
  background: ${ConstColor.Success};
  border-radius: 4px;
  padding: 4px 15px;
  font-size: 9px;
  font-weight: bold;
  color: white;
  display: inline-block;
  margin-bottom: 10px;
`;

const OperationNotInputLabel = styled.div`
  margin-right: 10px;
  background: #f13333;
  border-radius: 4px;
  padding: 4px 15px;
  font-size: 9px;
  font-weight: bold;
  color: white;
  display: inline-block;
  margin-bottom: 10px;
`;

const OperationDateLabel = styled.div`
  font-size: 11px;
  font-weight: normal;
  color: #a4a4a4;
  display: inline-block;
`;

const OperationTitleLabel = styled.div`
  font-size: 15px;
  font-weight: bold;
  color: #333;
`;

const ReportDetailLink = styled(GoldLink)`
  margin-right: 10px;
  margin-top: 10px;
  font-size: 12px;
  text-align: center;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/customer_contract_operation_detail.tsx